<template>
  <div ref="root" class="alp" @keydown="onKeydown">
    <LgButton size="sm" variant="ghost" :aria-pressed="open" @click="toggleOpen">
      <template #icon><Filter :size="14" aria-hidden="true" /></template>
      Add label
    </LgButton>

    <div v-if="open" class="panel" role="dialog" aria-label="Match a label value">
      <template v-if="!pickedKey">
        <ul v-if="availableKeys.length" class="key-list" role="listbox" aria-label="Labels">
          <li v-for="k in availableKeys" :key="k">
            <button type="button" class="key-item" @click="pickKey(k)">
              <span class="key-name">{{ labelDisplayName(k) }}</span>
            </button>
          </li>
        </ul>
        <div v-else class="panel-note">No more labels to match on this metric.</div>
        <!-- Escape hatch: a key the catalog doesn't know about (metric renamed,
             catalog window empty, …) can still be typed. -->
        <form class="custom-row" @submit.prevent="pickCustomKey">
          <LgInput
            v-model="customKey"
            class="custom-input"
            placeholder="or type a label key…"
            aria-label="Custom label key"
          />
          <LgButton size="sm" variant="ghost" type="submit" :disabled="!customKey.trim()">
            Next
          </LgButton>
        </form>
      </template>

      <template v-else>
        <button type="button" class="back-btn" @click="goBack">
          <ChevronLeft :size="14" aria-hidden="true" />
          {{ labelDisplayName(pickedKey) }} =
        </button>

        <LgInput
          v-model="valueText"
          class="value-input"
          placeholder="label value…"
          aria-label="Label value"
          @keydown.enter.prevent="commit"
        />

        <div v-if="valueLoading" class="panel-note">Loading values…</div>
        <ul v-else-if="values.length" class="suggest-list" role="listbox" aria-label="Label values">
          <li v-for="v in values" :key="v">
            <button type="button" class="suggest-item" @click="valueText = v">{{ v }}</button>
          </li>
        </ul>
        <div v-else-if="valueLoaded" class="panel-note">No recent values found — type one.</div>

        <LgButton
          size="sm"
          variant="primary"
          class="add-btn"
          :disabled="!valueText.trim()"
          @click="commit"
        >
          Add
        </LgButton>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from 'vue';
import { Filter, ChevronLeft } from 'lucide-vue-next';
import { api } from '../../api.js';
import { labelDisplayName } from '../../lib/metric-exploration.js';
import LgButton from '../../ui/LgButton.vue';
import LgInput from '../../ui/LgInput.vue';

/**
 * Exact-match (`=` only) label selector for the alert form. Alert label
 * selectors are a plain `{key: value}` map on the wire — no ≠/regex — so this
 * is a simplified sibling of MetricLabelDropdown: pick a key (from the
 * metric's known label keys, or type one), then a value with suggestions
 * discovered via the §2.4 instant query.
 */
const props = defineProps<{
  /** Metric identity for value discovery (name is enough; source/target narrow it). */
  metricName: string;
  source?: string;
  target?: string;
  /** Keys offered in the list (parent includes `__target__` and excludes `le`). */
  labelKeys: string[];
  /** Already-selected keys, hidden from the list. */
  existingKeys: string[];
}>();

const emit = defineEmits<{ add: [key: string, value: string] }>();

const open = ref(false);
const pickedKey = ref<string | null>(null);
const customKey = ref('');
const valueText = ref('');
const values = ref<string[]>([]);
const valueLoading = ref(false);
const valueLoaded = ref(false);
let seq = 0;

const availableKeys = computed(() =>
  props.labelKeys.filter((k) => !props.existingKeys.includes(k)),
);

function toggleOpen() {
  open.value ? close() : openPanel();
}
function openPanel() {
  pickedKey.value = null;
  customKey.value = '';
  valueText.value = '';
  resetValues();
  open.value = true;
}
function close() {
  open.value = false;
}
function goBack() {
  pickedKey.value = null;
  resetValues();
}
function resetValues() {
  seq++;
  values.value = [];
  valueLoading.value = false;
  valueLoaded.value = false;
}

function pickKey(k: string) {
  pickedKey.value = k;
  valueText.value = '';
  resetValues();
  loadValues(k);
}
function pickCustomKey() {
  const k = customKey.value.trim();
  if (k) pickKey(k);
}

/** Discover recent values for a label via an instant query grouped by it. */
function loadValues(label: string) {
  const s = ++seq;
  valueLoading.value = true;
  const now = Date.now();
  api.metrics
    .instant({
      source: props.source || undefined,
      name: props.metricName,
      target: props.target || undefined,
      to: now,
      evalWindow: 60 * 60 * 1000,
      agg: 'last',
      groupBy: [label],
    })
    .then((res) => {
      if (s !== seq) return;
      const set = new Set<string>();
      for (const r of res.results) {
        const v = r.labels[label];
        if (v !== undefined) set.add(v);
      }
      values.value = [...set].sort();
    })
    .catch(() => {
      /* suggestions are best-effort; typing still works */
    })
    .finally(() => {
      if (s !== seq) return;
      valueLoading.value = false;
      valueLoaded.value = true;
    });
}

function commit() {
  if (!pickedKey.value) return;
  const value = valueText.value.trim();
  if (!value) return;
  emit('add', pickedKey.value, value);
  close();
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value) {
    e.preventDefault();
    close();
  }
}

const root = ref<HTMLElement | null>(null);
function onDocPointer(e: MouseEvent) {
  if (open.value && root.value && !root.value.contains(e.target as Node)) close();
}
document.addEventListener('pointerdown', onDocPointer);
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocPointer));
</script>

<style scoped>
.alp {
  position: relative;
  display: inline-flex;
}
.panel {
  position: absolute;
  z-index: var(--lg-z-menu);
  top: calc(100% + var(--lg-space-1));
  left: 0;
  width: 260px;
  max-width: calc(100vw - var(--lg-space-6));
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
  padding: var(--lg-space-2);
  border-radius: var(--lg-radius-md);
  border: 1px solid var(--lg-border);
  background: var(--lg-bg-raised);
  box-shadow: var(--lg-shadow-2);
}
@media (max-width: 768px) {
  .panel {
    position: fixed;
    left: var(--lg-space-2);
    right: var(--lg-space-2);
    bottom: var(--lg-space-2);
    top: auto;
    width: auto;
    max-width: none;
    max-height: 75vh;
    overflow-y: auto;
  }
}
.panel-note {
  padding: var(--lg-space-2);
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-sm);
}
.key-list,
.suggest-list {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 200px;
  overflow-y: auto;
}
.key-item {
  display: flex;
  width: 100%;
  padding: var(--lg-space-2);
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.key-item:hover {
  background: var(--lg-bg-hover);
}
.key-name {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
}
.suggest-item {
  display: block;
  width: 100%;
  padding: var(--lg-space-1) var(--lg-space-2);
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text);
  font: inherit;
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.suggest-item:hover {
  background: var(--lg-bg-hover);
}
.custom-row {
  display: flex;
  gap: var(--lg-space-2);
  align-items: center;
}
.custom-input {
  flex: 1;
  min-width: 0;
}
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--lg-space-1);
  align-self: flex-start;
  border: none;
  background: transparent;
  color: var(--lg-text-dim);
  font: inherit;
  font-family: var(--lg-font-mono);
  padding: var(--lg-space-1);
  cursor: pointer;
}
.back-btn:hover {
  color: var(--lg-text);
}
.add-btn {
  align-self: flex-end;
}
</style>
